import z from 'zod';

const montageStatusSchema = z.enum(['processing', 'ready', 'failed']);
export type MontageStatus = z.infer<typeof montageStatusSchema>;

const montageEventRefSchema = z.discriminatedUnion('type', [
    z.object({
        type: z.literal('game_event'),
        key: z.string(),
        name: z.string(),
        categoryKey: z.string().optional(),
        categoryName: z.string().optional(),
    }),
    z.object({
        type: z.literal('mod'),
        key: z.string(),
        name: z.string(),
        categoryKey: z.string().optional(),
        categoryName: z.string().optional(),
    }),
    z.object({
        type: z.literal('manual'),
        name: z.string().optional(),
    }),
]);
export type MontageEventRef = z.infer<typeof montageEventRefSchema>;

const montagePlaybackSourceSchema = z.object({
    type: z.enum(['hls', 'mp4']),
    url: z.string(),
    mimeType: z.string().optional(),
});

const montagePlaybackSchema = z.object({
    posterUrl: z.string().nullable(),
    sources: z.array(montagePlaybackSourceSchema),
});
export type MontagePlayback = z.infer<typeof montagePlaybackSchema>;

const montageSourceVideoRefSchema = z.object({
    id: z.string(),
    externalId: z.string().optional(),
    title: z.string().optional(),
    recordedAt: z.string().optional(),
    thumbnailUrl: z.string().nullable().optional(),
});

const montageSourceRangeSchema = z.object({
    sourceStartTimeSec: z.number(),
    sourceEndTimeSec: z.number(),
});

const montageClipDetailSchema = z.object({
    id: z.string(),
    index: z.number(),
    label: z.string(),
    thumbnailUrl: z.string().nullable(),
    montageStartTimeSec: z.number(),
    durationSec: z.number(),
    trigger: montageEventRefSchema.nullable(),
    sourceVideo: montageSourceVideoRefSchema,
    sourceRange: montageSourceRangeSchema,
});
export type MontageClipDetail = z.infer<typeof montageClipDetailSchema>;

const montageSummaryShape = {
    id: z.string(),
    status: montageStatusSchema,
    title: z.string(),
    fileName: z.string(),
    createdAt: z.string(),
    generatedAt: z.string().nullable(),
    recordedAt: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    durationSec: z.number().nullable(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    titleIds: z.array(z.number()),
    gameIds: z.array(z.number()),
    primaryEvent: montageEventRefSchema.nullable(),
    clipCount: z.number(),
};

export const montageSummarySchema = z.object(montageSummaryShape).describe('MontageSummary');
export type MontageSummary = z.infer<typeof montageSummarySchema>;

export const getMontagesResponseSchema = z
    .object({
        montages: z.array(montageSummarySchema),
        count: z.number(),
        nextCursor: z.string().nullable(),
    })
    .describe('GetMontagesResponse');
export type GetMontagesResponse = z.infer<typeof getMontagesResponseSchema>;

export const montageDetailSchema = z
    .object({
        ...montageSummaryShape,
        playback: montagePlaybackSchema.nullable(),
        clips: z.array(montageClipDetailSchema),
    })
    .describe('MontageDetail');
export type MontageDetail = z.infer<typeof montageDetailSchema>;

export const getMontageResponseSchema = z
    .object({
        montage: montageDetailSchema,
    })
    .describe('GetMontageResponse');
export type GetMontageResponse = z.infer<typeof getMontageResponseSchema>;
